import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag } from "lucide-react";
import { eur } from "@/lib/api";
import { useWishlist } from "@/context/WishlistContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { has, toggle } = useWishlist();
  const { addItem } = useCart();
  const onSale = product.sale_price != null;
  const price = onSale ? product.sale_price : product.price;
  const soldOut = (product.total_stock ?? 1) <= 0;
  const badge = soldOut ? "SOLD OUT" : product.badge;
  const lowStock = !soldOut && (product.total_stock ?? 99) <= 5;

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (soldOut) return;
    const color = product.colors?.[0];
    const size = product.sizes?.[0];
    const variant = product.variants?.find((v) => v.color === color && v.size === size);
    if (!variant || variant.stock <= 0) {
      toast.error("Επίλεξε μέγεθος στη σελίδα προϊόντος");
      return;
    }
    addItem({
      product_id: product.id, slug: product.slug, name: product.name, color, size,
      price, quantity: 1, image: product.image_front, max_stock: variant.stock,
    });
  };

  return (
    <Link
      to={`/product/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="group block animate-fade-up"
      style={{ animationDelay: `${Math.min(index * 60, 480)}ms` }}
    >
      <div className="relative overflow-hidden bg-[#18181C] aspect-[3/4] rounded-lg">
        <img
          src={product.image_front}
          alt={product.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover img-zoom group-hover:opacity-0 transition-opacity duration-500"
        />
        {product.image_back && (
          <img
            src={product.image_back}
            alt={`${product.name} back`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          />
        )}

        {badge && (
          <span
            className={`absolute top-3 left-3 z-10 px-2.5 py-1 text-[10px] font-mono font-bold tracking-widest uppercase ${
              soldOut ? "bg-zinc-700 text-zinc-300" :
              badge.includes("SALE") ? "bg-red-600 text-white" :
              badge.includes("LIMITED") ? "bg-[#FF2E4C] text-white" :
              "bg-white text-black"
            }`}
          >
            {badge}
          </span>
        )}

        <button
          data-testid="product-card-wishlist-button"
          onClick={(e) => { e.preventDefault(); toggle(product.id, product.name); }}
          className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full glass hover:bg-[#FF2E4C] transition-colors"
          aria-label="wishlist"
        >
          <Heart className={`w-4 h-4 ${has(product.id) ? "fill-[#FF2E4C] text-[#FF2E4C]" : "text-white"}`} />
        </button>

        {!soldOut && (
          <button
            data-testid="product-card-quick-add-button"
            onClick={quickAdd}
            className="absolute bottom-0 left-0 right-0 z-10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 btn-red py-3 text-xs font-bold tracking-widest uppercase flex items-center justify-center gap-2"
          >
            <ShoppingBag className="w-4 h-4" /> ΓΡΗΓΟΡΗ ΠΡΟΣΘΗΚΗ
          </button>
        )}
      </div>

      <div className="pt-3 pb-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display font-bold text-sm sm:text-base uppercase leading-tight tracking-tight group-hover:text-[#FF2E4C] transition-colors">
            {product.name}
          </h3>
        </div>
        <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">{product.category}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <span className={`font-semibold ${onSale ? "text-[#FF2E4C]" : "text-white"}`}>{eur(price)}</span>
          {onSale && <span className="text-sm text-zinc-500 line-through">{eur(product.price)}</span>}
        </div>
        {lowStock && (
          <p className="text-[11px] font-mono uppercase tracking-wider text-[#FF2E4C] mt-1">
            Τελευταία κομμάτια
          </p>
        )}
      </div>
    </Link>
  );
}
